/**
 * PrivacyPolicy — public /privacy page (PR #33, GO_LIVE_PUNCHLIST C21).
 *
 * Drafted to cover Australian Privacy Act 1988 obligations and the
 * Australian Privacy Principles (APP 1, 5, 6, 11, 12, 13). Specifically
 * referenced from ESG_01_COMPLIANCE_MEMO.md §2 — the e-sign signing flow's
 * APP 5 disclosure must be backed by a public-facing policy.
 *
 * **REVIEW WITH COUNSEL BEFORE CUTOVER.** This is starter copy; some
 * clauses may need tightening for Solara's specific lender / introducer /
 * client data flows. The structure is intentionally close to a typical AU
 * fintech policy.
 *
 * Design: long-form readable layout, navy + gold accents matching the rest
 * of the brand. No login required.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

const LOGO_HORIZONTAL = "/logos/solara-horizontal.png";
const LAST_UPDATED = "8 May 2026";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy — Solara Global Capital Partners";
  }, []);

  return (
    <div className="min-h-screen bg-[#fafaf9] flex flex-col">
      <header className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="block">
            <img src={LOGO_HORIZONTAL} alt="Solara Global Capital Partners" className="h-9" />
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/terms" className="text-gray-600 hover:text-[#bd9a59]">Terms</Link>
            <Link to="/" className="text-gray-600 hover:text-[#bd9a59]">Home</Link>
          </nav>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-10 sm:py-12">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 sm:p-10">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-6 h-6 text-[#bd9a59]" />
            <h1 className="text-3xl font-semibold text-[#021d49]">Privacy Policy</h1>
          </div>
          <p className="text-sm text-gray-500 mb-8">Last updated: {LAST_UPDATED}</p>

          <Section title="1. Who we are">
            <p>
              Aussie Prime Land Pty Ltd (ACN 606 570 932), trading as <strong>Solara Global Capital Partners</strong>, is a commercial
              finance brokerage based in Sydney, NSW that arranges capital on behalf of its clients. References to "we", "us" or
              "our" in this policy mean Aussie Prime Land Pty Ltd and any related entities.
            </p>
            <p>
              This policy describes how we collect, hold, use and disclose your personal information in accordance with the
              Australian <em>Privacy Act 1988</em> (Cth) and the Australian Privacy Principles (APPs).
            </p>
          </Section>

          <Section title="2. What information we collect">
            <p>We collect information that's reasonably necessary to provide capital advisory services, including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Identity information — full legal name, date of birth, drivers licence or passport details, residential address.</li>
              <li>Contact details — email addresses, phone numbers, postal addresses.</li>
              <li>Financial information — assets, liabilities, income, bank statements, tax returns, BAS, asset valuations.</li>
              <li>Transaction information — proposed loan structure, security details, exit strategy, supporting documentation.</li>
              <li>Company information — ACN/ABN, directors, structure, financial statements, trust deeds where applicable.</li>
              <li>
                Electronic-signature metadata — when you sign a Solara document electronically, we record the time, your IP
                address, browser user-agent, the SHA-256 hash of the document signed, and the typed name + signature image you
                provided. This audit trail is appended to the executed copy of the document for tamper-evidence purposes
                (Australian <em>Electronic Transactions Act 1999</em> compliance).
              </li>
              <li>
                Email and document tracking — open events on transactional email (e.g. mandate delivery confirmations) via a
                tracking pixel, and access events on documents we share via Google Drive.
              </li>
            </ul>
          </Section>

          <Section title="3. How we collect it">
            <p>We collect personal information in several ways:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Directly from you when you complete our online application form, sign documents, upload supporting files, or correspond with us.</li>
              <li>From your guarantors, accountants, solicitors, or other professional advisers when you authorise them to provide it.</li>
              <li>From credit reporting bodies and identity verification providers, where you have consented or where authorised by law.</li>
              <li>From third-party data sources such as ASIC, PEXA, valuers, and property research providers.</li>
            </ul>
          </Section>

          <Section title="4. Why we collect it">
            <p>We use your personal information to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Assess your capital requirements and prepare loan applications on your behalf.</li>
              <li>Engage and negotiate with banks, private credit funds, and other capital sources.</li>
              <li>Verify your identity in accordance with anti-money-laundering / counter-terrorism-financing obligations.</li>
              <li>Issue, sign, and administer mandate agreements, term sheets, invoices, and other commercial documents.</li>
              <li>Communicate with you about your transaction, including via email, SMS, and our client portal.</li>
              <li>Comply with legal, regulatory and contractual obligations, including audit and tax requirements.</li>
              <li>Improve our service, train staff, and conduct internal analysis.</li>
            </ul>
          </Section>

          <Section title="5. Who we share it with">
            <p>We may disclose your personal information to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Capital sources</strong> — banks, credit funds, private lenders we engage on your behalf.</li>
              <li><strong>Service providers</strong> — valuers, solicitors, accountants, identity verification providers, IT and storage providers.</li>
              <li><strong>Introducers and referrers</strong> — limited to confirmation of progress and referral fee reconciliation; we do not share supporting documentation with introducers without your consent.</li>
              <li><strong>Government and regulatory bodies</strong> — when required or authorised by law.</li>
              <li><strong>Our group entities and successors</strong> — in connection with corporate transactions, restructures, or assignments.</li>
            </ul>
            <p>We do not sell, rent, or trade your personal information.</p>
          </Section>

          <Section title="6. Where it's stored">
            <p>
              Your information is stored in Australia (MongoDB Atlas, Sydney region) and in Google Workspace (Drive, Gmail) hosted by
              Google in jurisdictions that include Australia and the United States. Where information is held overseas, we take
              reasonable steps to ensure equivalent protection consistent with APP 8.
            </p>
            <p>
              Document files you upload are stored in Google Drive within Solara's Google Workspace tenant, accessible only to
              authorised Solara staff and any specific introducers, lenders, or advisers you have authorised.
            </p>
          </Section>

          <Section title="7. How long we keep it">
            <p>
              We retain personal information for the life of our engagement with you plus seven (7) years, consistent with our
              record-keeping obligations under the <em>Corporations Act 2001</em> and tax law. Electronic-signature audit trails
              are retained for the same period to ensure tamper-evidence of signed documents.
            </p>
            <p>
              When we no longer need information and there is no legal obligation to retain it, we delete or de-identify it.
            </p>
          </Section>

          <Section title="8. Security">
            <p>We take reasonable steps to protect your personal information from unauthorised access, modification, disclosure, or loss, including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>TLS 1.2+ encryption for all data in transit.</li>
              <li>Encryption-at-rest in our database and storage providers.</li>
              <li>Access controls including role-based permissions and one-time passcodes for electronic signing.</li>
              <li>Audit logging of administrative actions and document access.</li>
              <li>Regular review of access by staff and revocation when staff change roles or depart.</li>
            </ul>
            <p>
              No method of internet transmission or electronic storage is 100% secure. If we believe a security incident has affected
              your personal information, we will notify you in accordance with the Notifiable Data Breaches scheme.
            </p>
          </Section>

          <Section title="9. Your rights">
            <p>Subject to limited legal exceptions, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access the personal information we hold about you.</li>
              <li>Request correction of any information you believe is inaccurate, out-of-date, incomplete, or misleading.</li>
              <li>Request that we stop using your information for direct marketing.</li>
              <li>Make a complaint about how we have handled your personal information.</li>
            </ul>
            <p>
              To exercise any of these rights, email us at{" "}
              <a href="mailto:privacy@sgcap.com.au" className="text-[#bd9a59] hover:underline">privacy@sgcap.com.au</a>.
              We will respond within 30 days.
            </p>
            <p>
              If you are not satisfied with our response, you may complain to the Office of the Australian Information Commissioner
              (OAIC) at{" "}
              <a href="https://www.oaic.gov.au" className="text-[#bd9a59] hover:underline" target="_blank" rel="noopener noreferrer">oaic.gov.au</a>.
            </p>
          </Section>

          <Section title="10. Cookies and tracking">
            <p>
              The Solara online portal uses essential first-party cookies for session management and CSRF protection. We do not use
              third-party advertising or behaviour-tracking cookies. Transactional emails we send may contain a tracking pixel that
              records open events for the purpose of confirming delivery; this is described on each individual email's audit trail.
            </p>
          </Section>

          <Section title="11. Changes to this policy">
            <p>
              We may update this policy from time to time. The "Last updated" date at the top of this page reflects the most recent
              revision. Material changes will be communicated to active customers by email.
            </p>
          </Section>

          <Section title="12. Contact us">
            <p>
              For privacy enquiries, contact our Privacy Officer at{" "}
              <a href="mailto:privacy@sgcap.com.au" className="text-[#bd9a59] hover:underline">privacy@sgcap.com.au</a> or:
            </p>
            <address className="not-italic mt-2 text-gray-700">
              Privacy Officer<br />
              Aussie Prime Land Pty Ltd t/a Solara Global Capital Partners<br />
              Level 1, 60 Martin Place<br />
              Sydney NSW 2000
            </address>
          </Section>
        </div>
      </main>

      <footer className="py-6 text-center text-xs text-gray-400">
        Solara Global Capital Partners · sgcap.com.au · <Link to="/terms" className="hover:text-[#bd9a59]">Terms</Link>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8 last:mb-0">
      <h2 className="text-lg font-semibold text-[#021d49] mb-3 border-b border-gray-100 pb-2">{title}</h2>
      <div className="space-y-3 text-sm text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}
